import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { HttpAdapter } from 'src/app/adapters/http/http.adapter';
import { MSEndpoint, MSHttpEvent, MSQuery, MSClientNoServerAddressCached } from 'src/app/adapters/http/utils/http.utils';
import { UserStorageUseCase } from '../../user-storage/user-storage.use-case';
import { getMSApiUrl } from '../utils/http.utils';
import { EnvironmentAdapter } from 'src/app/adapters/environment/environment.adapter';

@Injectable({
  providedIn: 'root'
})
export class MSApiHttpUseCase {

  constructor(
    private environmentAdapter: EnvironmentAdapter,
    private userStorageUseCase: UserStorageUseCase,
    private httpAdapter: HttpAdapter,
  ) {}

  /**
   * The function returns an Observable that makes a GET request to a specified endpoint and returns
   * the response in a specified format.
   * @param endpoint - The `endpoint` parameter is an object that represents the
   * specific endpoint or API route that you want to make a GET request to.
   * @param [query] - The `query` parameter is an optional object that contains additional
   * parameters or filters to be included in the HTTP request. It is used to customize the request
   * and retrieve specific data from the server.
   * @returns an Observable of type `MSHttpEvent.Spec`.
   */
  get(endpoint: MSEndpoint, query?: MSQuery): Observable<MSHttpEvent.Spec> {
    const getObservable = this.userStorageUseCase.getServerAddress().pipe(
      mergeMap((serverAddress) => {
        if (!serverAddress) {
          const errorEvent = new MSHttpEvent.Error(MSClientNoServerAddressCached, 'Server address is not defined');
          return of(errorEvent);
        }

        const url = getMSApiUrl(serverAddress, endpoint);
        const httpGetObservable = this.httpAdapter.get(url, query);

        return httpGetObservable;
      })
    );

    return getObservable;
  }

  getWithLoadBalancerAddress(endpoint: MSEndpoint, query?: MSQuery): Observable<MSHttpEvent.Spec> {
    const getObservable = this.environmentAdapter.getEnvironment().pipe(
      mergeMap((environment) => {
        const url = getMSApiUrl(environment.serverUrl, endpoint);
        const httpGetObservable = this.httpAdapter.get(url, query);

        return httpGetObservable;
      })
    );

    return getObservable;
  }
}
